import { brazeIdentity } from '@services/braze';
import { analytics } from '@utils/analytics';
import { extraQueryParams } from '@utils/constants';
import { takePendingOfferPath } from '@utils/pendingOffer';
import { toSafeReturnPath } from '@utils/returnUrl';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Add interface for the auth state
interface AuthState {
  returnUrl: string;
}

function CallbackSignIn() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      const user = auth.user as { state?: AuthState };
      // Sign-in state can be lost (e.g. email verified in another tab); resume a recent offer
      let redirectPath =
        toSafeReturnPath(user?.state?.returnUrl) ??
        takePendingOfferPath() ??
        '/';
      const linkStatus = searchParams.get('linkStatus');
      if (linkStatus && redirectPath.startsWith('/auth/partner/success')) {
        const sep = redirectPath.includes('?') ? '&' : '?';
        redirectPath = `${redirectPath}${sep}linkStatus=${encodeURIComponent(linkStatus)}`;
      }
      navigate(redirectPath, { replace: true });

      analytics.logEvent('user_logged_in');
      brazeIdentity.identifyUser(auth.user);
    }

    if (!auth.isLoading && !auth.isAuthenticated) {
      auth
        .signinRedirect({
          extraQueryParams,
        })
        .catch((error) => {
          console.error('Sign-in error:', error);
          navigate('/', {
            state: { error: t('auth.authFailed') },
          });
        });
    }
  }, [auth, navigate]);

  return (
    <div className="flex items-center justify-center h-screen text-lg font-extralight">
      {t('auth.processingLogin')}
    </div>
  );
}

export { CallbackSignIn };
